import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';

import { AuthProvider, useAuth } from './context/AuthContext';
import { FincaProvider } from './context/FincaContext';
import { PermisosProvider } from './context/PermisosContext';
import RutaConPermiso from './components/RutaConPermiso';
import MainLayout from './layouts/MainLayout';
import Monitoreo from './pages/Monitoreo';
import Ayuda from './pages/Ayuda';
import Parametros from './pages/Parametros';
import ParametrosConfig from './pages/ParametrosConfig';
import DemoApp from './pages/DemoApp';
import DescargarApp from './pages/DescargarApp';
import Demos from './pages/Demos';
import Planes from './pages/Planes';
import Suscripciones from './pages/Suscripciones';
import Pagos from './pages/Pagos';
import Pagar from './pages/Pagar';
import ConfigPagos from './pages/ConfigPagos';

// Autenticación
import Login from './pages/Login';

// Módulos Camaronera
import Dashboard from './pages/Dashboard';
import RegistroDiario from './pages/RegistroDiario';
import Configuracion from './pages/Configuracion';
import Mapas from './pages/Mapas'; // ✅ Importamos el nuevo wrapper
import Muestreos from './pages/Muestreos';
import Cosechas from './pages/Cosechas';
import ReportesMetricas from './pages/ReportesMetricas';
import RegistroBiologo from './pages/RegistroBiologo';

// Módulos Alimentación Automática
import Actividades from './pages/alimentacion/Actividades';
import TiposAlerta from './pages/alimentacion/TiposAlerta';
import Diagnosticos from './pages/alimentacion/Diagnosticos';
import ReportesAlimentacion from './pages/alimentacion/ReportesAlimentacion';

ReactGA.initialize('G-W3VHSQ7J3R');

function AnalyticsTracker() {
  const location = useLocation();
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: location.pathname + location.search });
  }, [location]);
  return null;
}

function RutaPrivada({ children }) {
  const { usuario } = useAuth();
  if (!usuario) return <Navigate to="/login" replace />;
  return children;
}

function RedirigirPorRol({ children }) {
  const { usuario } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!usuario) return;
    if (usuario.rol === 'operador' || usuario.rol === 'auxiliar') {
      const path = window.location.pathname;
      if (path === '/' || path === '/dashboard') {
        navigate('/alimentacion/actividades', { replace: true });
      }
    }
  }, [usuario, navigate]);

  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <FincaProvider>
        <PermisosProvider>
          <BrowserRouter>
            <AnalyticsTracker />
            <Routes>
              {/* Rutas públicas */}
              <Route path="/login" element={<Login />} />
              <Route path="/demo" element={<DemoApp />} />
              <Route path="/descargar-app" element={<DescargarApp />} />

              {/* Rutas privadas dentro del layout principal */}
              <Route
                path="/"
                element={
                  <RutaPrivada>
                    <RedirigirPorRol>
                      <MainLayout />
                    </RedirigirPorRol>
                  </RutaPrivada>
                }
              >
                <Route index element={
                  <RutaConPermiso permiso="dashboard"><Dashboard /></RutaConPermiso>
                } />
                
                <Route path="parametros" element={
                  <RutaConPermiso permiso="operacion.parametros"><Parametros /></RutaConPermiso>
                } />
                <Route path="parametros/config" element={
                  <RutaConPermiso permiso="configuracion.piscinas"><ParametrosConfig /></RutaConPermiso>
                } />

                <Route path="registro-diario" element={
                  <RutaConPermiso permiso="operacion.registro-diario"><RegistroDiario /></RutaConPermiso>
                } />
                <Route path="configuracion" element={
                  <RutaConPermiso permiso="configuracion"><Configuracion /></RutaConPermiso>
                } />
                
                {/* ✅ Ruta unificada para el Mapa */}
                <Route path="mapa" element={
                  <RutaConPermiso permiso="operacion.piscinas"><Mapas /></RutaConPermiso>
                } />

                <Route path="muestreos" element={
                  <RutaConPermiso permiso="operacion.muestreos"><Muestreos /></RutaConPermiso>
                } />
                <Route path="cosechas" element={
                  <RutaConPermiso permiso="operacion.cosechas"><Cosechas /></RutaConPermiso>
                } />
                <Route path="reportes" element={
                  <RutaConPermiso permiso="reportes"><ReportesMetricas /></RutaConPermiso>
                } />
                <Route path="registro-biologo" element={
                  <RutaConPermiso permiso="operacion.registro-biologo"><RegistroBiologo /></RutaConPermiso>
                } />

                <Route path="alimentacion/actividades" element={
                  <RutaConPermiso permiso="alimentacion.actividades"><Actividades /></RutaConPermiso>
                } />
                <Route path="alimentacion/tipos-alerta" element={
                  <RutaConPermiso permiso="alimentacion.tipos-alerta"><TiposAlerta /></RutaConPermiso>
                } />
                <Route path="alimentacion/diagnosticos" element={
                  <RutaConPermiso permiso="alimentacion.diagnosticos"><Diagnosticos /></RutaConPermiso>
                } />
                <Route path="alimentacion/reportes" element={
                  <RutaConPermiso permiso="alimentacion.reportes"><ReportesAlimentacion /></RutaConPermiso>
                } />

                <Route path="monitoreo" element={
                  <RutaConPermiso permiso="monitoreo.ver"><Monitoreo /></RutaConPermiso>
                } />

                <Route path="demos" element={<Demos />} />
                <Route path="planes" element={<Planes />} />
                <Route path="suscripciones" element={<Suscripciones />} />
                <Route path="pagos" element={<Pagos />} />
                <Route path="pagar" element={<Pagar />} />
                <Route path="config-pagos" element={<ConfigPagos />} />
                <Route path="ayuda" element={<Ayuda />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </PermisosProvider>
      </FincaProvider>
    </AuthProvider>
  );
}